import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ndfApi } from '../../services/ndf-api';
import type { Depense } from '../../types/ndf';
import { BoutonSecondaire, Champ, dateFr, Etiquette, Selection } from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { erreurGlobale } from '../../components/ndf/ui/erreurs-champs';
import { PanneauAssociation } from '../../components/ndf/PanneauAssociation';
import { useToast } from '../../context/ToastContext';

/**
 * Mes dépenses.
 *
 * Le titre d'une ligne est la CATÉGORIE et la DATE, pas le montant : c'est par
 * là qu'on retrouve une dépense — « mon taxi de mardi », jamais « ma dépense de
 * 43,20 € ». Le montant reste affiché, à droite, où on le compare d'un coup
 * d'œil sans qu'il domine la lecture.
 *
 * Des cartes, pas un tableau : à 375 px de large, un tableau se lit en biais.
 */

const STATUTS: Record<string, { libelle: string; ton: 'neutre' | 'actif' | 'positif' | 'alerte' | 'attention' }> = {
  brouillon: { libelle: 'Brouillon', ton: 'neutre' },
  a_soumettre: { libelle: 'À soumettre', ton: 'attention' },
  soumise: { libelle: 'En attente d’approbation', ton: 'actif' },
  approuvee_manager: { libelle: 'Approuvée par le manager', ton: 'actif' },
  approuvee_rh: { libelle: 'À rembourser', ton: 'positif' },
  remboursee: { libelle: 'Remboursée', ton: 'positif' },
  rejetee: { libelle: 'Refusée', ton: 'alerte' },
};

function euros(v: number) {
  return v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}
/** Périodes proposées — celles qu'on utilise vraiment pour retrouver une dépense. */
const PERIODES = [
  { cle: 'toutes', libelle: 'Toutes les périodes' },
  { cle: 'mois', libelle: 'Ce mois-ci' },
  { cle: 'mois_dernier', libelle: 'Le mois dernier' },
  { cle: 'trimestre', libelle: 'Les 3 derniers mois' },
  { cle: 'annee', libelle: 'Cette année' },
];

function dansLaPeriode(date: string, periode: string): boolean {
  if (periode === 'toutes') return true;
  const d = new Date(date + 'T12:00:00');
  const now = new Date();
  const debutMois = new Date(now.getFullYear(), now.getMonth(), 1);
  switch (periode) {
    case 'mois':
      return d >= debutMois;
    case 'mois_dernier': {
      const debut = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return d >= debut && d < debutMois;
    }
    case 'trimestre':
      return d >= new Date(now.getFullYear(), now.getMonth() - 2, 1);
    case 'annee':
      return d >= new Date(now.getFullYear(), 0, 1);
    default:
      return true;
  }
}

export function NdfMesDepensesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  // Troisième chemin d'association (R-NDF-02) : depuis la liste, en masse.
  // C'est le geste de fin de mois — on range tout d'un coup, sans ouvrir une
  // note d'abord.
  const [rangement, setRangement] = useState(false);
  const [choisies, setChoisies] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'depenses'],
    queryFn: () => ndfApi.depenses.liste(),
    retry: false,
  });
  const { data: categoriesData } = useQuery({
    queryKey: ['ndf', 'categories'],
    queryFn: () => ndfApi.categories.lire(),
  });

  const depenses: Depense[] = data?.depenses ?? [];
  const categories = categoriesData?.categories ?? [];
  const nomCategorie = (id: string | null) =>
    categories.find((c) => c.id === id)?.libelle ?? null;

  const [filtres, setFiltres] = useState({
    categorie: '',
    periode: 'toutes',
    refacturable: '',
    remboursable: '',
    client: '',
    mission: '',
  });
  const [ouverts, setOuverts] = useState(false);

  const associables = useQuery({
    queryKey: ['ndf', 'notes', 'associables'],
    queryFn: () => ndfApi.notes.associables(),
    enabled: rangement,
    retry: false,
  });
  const notes = useQuery({
    queryKey: ['ndf', 'notes'],
    queryFn: () => ndfApi.notes.liste(),
    enabled: rangement,
    retry: false,
  });

  const rafraichir = () => {
    qc.invalidateQueries({ queryKey: ['ndf', 'depenses'] });
    qc.invalidateQueries({ queryKey: ['ndf', 'notes'] });
    qc.invalidateQueries({ queryKey: ['ndf', 'notes', 'associables'] });
  };

  const ranger = useMutation({
    mutationFn: async (noteId: string | 'nouvelle') => {
      // « Nouvelle note » enchaîne les deux opérations : créer puis ranger.
      // Demander à l'utilisateur de créer d'abord, puis de revenir ici, c'est
      // lui faire faire le travail de l'application.
      const cible =
        noteId === 'nouvelle' ? (await ndfApi.notes.creer(null)).note.id : noteId;
      await ndfApi.notes.associer(cible, [...choisies]);
      return cible;
    },
    onSuccess: (noteId) => {
      toast.success(`${choisies.size} dépense${choisies.size > 1 ? 's' : ''} rangée${choisies.size > 1 ? 's' : ''}.`);
      setChoisies(new Set());
      setRangement(false);
      rafraichir();
      navigate(`/notes-de-frais/note/${noteId}`);
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'Le rangement a échoué.'),
  });

  // Les listes proposées sont construites à partir des dépenses RÉELLES :
  // proposer un filtre qui ne renvoie rien n'aide personne, et le référentiel
  // complet des catégories est bien plus long que ce qu'un salarié déclare.
  const typesPresents = useMemo(() => {
    const ids = new Set(depenses.map((d) => d.categorie_id));
    return categories.filter((c) => ids.has(c.id)).sort((a, b) => a.ordre - b.ordre);
  }, [depenses, categories]);

  const clients = useMemo(
    () => [...new Set(depenses.map((d) => d.client_nom).filter(Boolean))] as string[],
    [depenses],
  );
  const missions = useMemo(
    () =>
      [
        ...new Set(
          depenses
            .filter((d) => !filtres.client || d.client_nom === filtres.client)
            .map((d) => d.mission_nom)
            .filter(Boolean),
        ),
      ] as string[],
    [depenses, filtres.client],
  );

  const visibles = depenses.filter((d) => {
    if (filtres.categorie && d.categorie_id !== filtres.categorie) return false;
    if (!dansLaPeriode(d.date_depense, filtres.periode)) return false;
    if (filtres.refacturable === 'oui' && !d.facturable) return false;
    if (filtres.refacturable === 'non' && d.facturable) return false;
    if (filtres.remboursable === 'oui' && !d.demande_remboursement) return false;
    if (filtres.remboursable === 'non' && d.demande_remboursement) return false;
    if (filtres.client && d.client_nom !== filtres.client) return false;
    if (filtres.mission && d.mission_nom !== filtres.mission) return false;
    return true;
  });

  const total = visibles.reduce((s, d) => s + d.montant_ttc, 0);
  const filtreActif =
    filtres.categorie || filtres.periode !== 'toutes' || filtres.refacturable ||
    filtres.remboursable || filtres.client || filtres.mission;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="font-titre text-lg font-bold text-nuit">Mes dépenses</h1>
          <p className="text-sm text-nuit/60">
            {visibles.length === 0
              ? 'Aucune dépense'
              : `${visibles.length} dépense${visibles.length > 1 ? 's' : ''} · ${euros(total)}`}
            {filtreActif && depenses.length !== visibles.length && ` (sur ${depenses.length})`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <BoutonSecondaire type="button" onClick={() => setOuverts((v) => !v)}>
            {ouverts ? 'Masquer les filtres' : 'Filtrer'}
          </BoutonSecondaire>
          <BoutonSecondaire
            type="button"
            onClick={() => {
              setRangement((v) => !v);
              setChoisies(new Set());
            }}
          >
            {rangement ? 'Fermer' : 'Ranger dans une note'}
          </BoutonSecondaire>
        </div>
      </header>

      {ouverts && (
        <section className="grid gap-3 rounded-lg border border-nuit/10 bg-white p-4 sm:grid-cols-2">
          <Champ label="Type de dépense">
            <Selection
              value={filtres.categorie}
              onChange={(e) => setFiltres({ ...filtres, categorie: e.target.value })}
            >
              <option value="">Tous</option>
              {typesPresents.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.libelle}
                </option>
              ))}
            </Selection>
          </Champ>

          <Champ label="Période">
            <Selection
              value={filtres.periode}
              onChange={(e) => setFiltres({ ...filtres, periode: e.target.value })}
            >
              {PERIODES.map((p) => (
                <option key={p.cle} value={p.cle}>
                  {p.libelle}
                </option>
              ))}
            </Selection>
          </Champ>

          <Champ label="Refacturable">
            <Selection
              value={filtres.refacturable}
              onChange={(e) => setFiltres({ ...filtres, refacturable: e.target.value })}
            >
              <option value="">Peu importe</option>
              <option value="oui">Oui</option>
              <option value="non">Non</option>
            </Selection>
          </Champ>

          <Champ label="Remboursement demandé">
            <Selection
              value={filtres.remboursable}
              onChange={(e) => setFiltres({ ...filtres, remboursable: e.target.value })}
            >
              <option value="">Peu importe</option>
              <option value="oui">Oui</option>
              <option value="non">Non</option>
            </Selection>
          </Champ>

          {/* Client et mission n'apparaissent que s'il y a de quoi filtrer. */}
          {clients.length > 0 && (
            <Champ label="Client">
              <Selection
                value={filtres.client}
                onChange={(e) => setFiltres({ ...filtres, client: e.target.value, mission: '' })}
              >
                <option value="">Tous</option>
                {clients.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Selection>
            </Champ>
          )}
          {missions.length > 0 && (
            <Champ label="Mission">
              <Selection
                value={filtres.mission}
                onChange={(e) => setFiltres({ ...filtres, mission: e.target.value })}
              >
                <option value="">Toutes</option>
                {missions.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Selection>
            </Champ>
          )}

          {filtreActif && (
            <div className="sm:col-span-2">
              <BoutonSecondaire
                type="button"
                onClick={() =>
                  setFiltres({
                    categorie: '',
                    periode: 'toutes',
                    refacturable: '',
                    remboursable: '',
                    client: '',
                    mission: '',
                  })
                }
              >
                Effacer les filtres
              </BoutonSecondaire>
            </div>
          )}
        </section>
      )}

      {rangement && (
        <section className="space-y-3">
          <PanneauAssociation
            groupes={associables.data?.groupes ?? []}
            depenses={associables.data?.depenses ?? []}
            choisies={choisies}
            onBasculer={(ids, actif) => {
              const suivant = new Set(choisies);
              for (const i of ids) (actif ? suivant.add(i) : suivant.delete(i));
              setChoisies(suivant);
            }}
            enCours={ranger.isPending}
            // Le panneau valide vers une note : ici il faut d'abord savoir
            // LAQUELLE, d'où le choix ci-dessous plutôt qu'un bouton unique.
            onValider={() => undefined}
            masquerValidation
          />

          {choisies.size > 0 && (
            <div className="sticky bottom-24 rounded-lg border border-ciel bg-white p-4 md:bottom-0">
              <p className="mb-2 text-sm text-nuit">
                {choisies.size} dépense{choisies.size > 1 ? 's' : ''} sélectionnée
                {choisies.size > 1 ? 's' : ''}. Dans quelle note ?
              </p>
              <div className="flex flex-wrap gap-2">
                <Selection
                  defaultValue=""
                  disabled={ranger.isPending}
                  onChange={(e) => e.target.value && ranger.mutate(e.target.value)}
                  className="max-w-xs"
                >
                  <option value="">Choisir…</option>
                  {(notes.data?.notes ?? [])
                    .filter((n) => n.statut === 'brouillon')
                    .map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.titre || n.numero}
                      </option>
                    ))}
                  <option value="nouvelle">＋ Nouvelle note de frais</option>
                </Selection>
              </div>
            </div>
          )}
        </section>
      )}

      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      {!isLoading && !error && depenses.length === 0 && (
        <div className="rounded-lg border border-dashed border-nuit/25 bg-white p-8 text-center">
          <p className="text-sm text-nuit/70">
            Photographiez un ticket avec le bouton <strong>+</strong> pour déclarer votre première
            dépense.
          </p>
        </div>
      )}

      {!isLoading && depenses.length > 0 && visibles.length === 0 && (
        <p className="rounded-lg border border-nuit/15 bg-white p-4 text-sm text-nuit/70">
          Aucune dépense ne correspond à ces filtres.
        </p>
      )}

      <ul className="space-y-2">
        {visibles.map((d) => {
          const statut = STATUTS[d.statut] ?? { libelle: d.statut, ton: 'neutre' as const };
          const siennes = d.alertes.filter((a) => a.niveau !== 'tag');
          return (
            <li key={d.id}>
              <Link
                to={`/notes-de-frais/depense/${d.id}`}
                className="block rounded-lg border border-nuit/10 bg-white p-3 active:translate-y-px"
              >
                <span className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block truncate font-titre font-bold text-nuit">
                      {nomCategorie(d.categorie_id) ?? 'Dépense'}
                      {d.sous_categorie_id && nomCategorie(d.sous_categorie_id)
                        ? ` · ${nomCategorie(d.sous_categorie_id)}`
                        : ''}
                    </span>
                    <span className="block text-sm text-nuit/60">{dateFr(d.date_depense)}</span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block font-medium text-nuit">{euros(d.montant_ttc)}</span>
                    {/* Le montant d'origine évite de se demander pourquoi ce
                        ticket de 50 CHF apparaît à 53,40 €. */}
                    {d.devise && d.devise !== 'EUR' && d.montant_original !== null && (
                      <span className="block text-xs text-nuit/60">
                        {d.montant_original} {d.devise}
                      </span>
                    )}
                  </span>
                </span>

                {d.facturable && (d.client_nom || d.mission_nom) && (
                  <span className="mt-1 block truncate text-sm text-nuit/70">
                    {d.client_nom ?? 'Client à préciser'}
                    {d.mission_nom ? ` · ${d.mission_nom}` : ''}
                  </span>
                )}

                <span className="mt-1 flex flex-wrap items-center gap-1">
                  <Etiquette ton={statut.ton}>{statut.libelle}</Etiquette>
                  {d.facturable && <Etiquette ton="actif">Refacturable</Etiquette>}
                  {siennes.length > 0 && (
                    <Etiquette ton="alerte">
                      {siennes.length} point{siennes.length > 1 ? 's' : ''} à vérifier
                    </Etiquette>
                  )}
                </span>

                {d.description && (
                  <span className="mt-1 block truncate text-sm text-nuit/60">{d.description}</span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
